import { Injectable, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { StorageService } from '../../services/storage.service';

const IDLE_THRESHOLD_MS = 90_000;
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

@Injectable()
export class TimerService implements OnDestroy {
    @Output() inactivity: EventEmitter<boolean> = new EventEmitter();

    subscription: Subscription = new Subscription();
    subInactivity: Subscription = new Subscription();

    private isTracking = true;
    private idleTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private visibilityHandler: () => void;
    private activityHandler: () => void;

    constructor(private storageService: StorageService) {
        this.visibilityHandler = () => {
            document.hidden ? this.pause() : this.resume();
        };

        this.activityHandler = () => {
            this.resume();
            this.scheduleIdle();
        };

        document.addEventListener('visibilitychange', this.visibilityHandler);
        ACTIVITY_EVENTS.forEach(e =>
            document.addEventListener(e, this.activityHandler, { passive: true })
        );
    }

    setTimer(session?: any, type?: string): void {
        const key = type === 'question' ? 'durationGlobal' : type === 'content' ? 'duration' : null;
        this.resetTimer();

        if (key && session && !session.doneAt) {
            const timerLocale = timer(2000, 15000);

            this.subscription = timerLocale.subscribe(() => {
                if (!this.isTracking) return;

                session[key] += 15;

                if (type === 'question') {
                    const params = {
                        notifyByMail: false,
                        requestCorrection: !!session?.doneAt,
                        company_id: this.storageService.get('company')?.id
                    };
                    session.update(params).subscribe();
                } else {
                    session.update(!!session?.doneAt, this.storageService.get('company')?.id).subscribe();
                }
            });

            this.scheduleIdle();
            this.checkInactivity();
        }
    }

    pause(): void {
        this.isTracking = false;
    }

    resume(): void {
        if (!document.hidden) {
            this.isTracking = true;
        }
    }

    private scheduleIdle(): void {
        if (this.idleTimeoutId !== null) clearTimeout(this.idleTimeoutId);
        this.idleTimeoutId = setTimeout(() => this.pause(), IDLE_THRESHOLD_MS);
    }

    resetTimer(): void {
        this.subscription.unsubscribe();
        this.subscription = new Subscription();
    }

    checkInactivity(): void {
        this.resetInactivity();
        const timerLocale = timer(602000, 602000);
        this.subInactivity = timerLocale.subscribe(() => {
            this.inactivity.emit(true);
            this.resetInactivity();
            this.resetTimer();
        });
    }

    resetInactivity(): void {
        this.subInactivity.unsubscribe();
        this.subInactivity = new Subscription();
    }

    ngOnDestroy(): void {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
        ACTIVITY_EVENTS.forEach(e => document.removeEventListener(e, this.activityHandler));
        if (this.idleTimeoutId !== null) clearTimeout(this.idleTimeoutId);
        this.resetTimer();
        this.resetInactivity();
    }
}
